import { useState } from 'react';
import { z } from 'zod';
import type { PhaseId, PhotoDto, UpdatePhotoCommand } from '@/api/generated';
import {
  useDeletePhoto,
  useDownloadPhoto,
  usePhotoThumbnailUrl,
  useRotatePhoto,
  useUpdatePhoto,
  useUploadPhoto,
} from '@/api/hooks';
import { Icon } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { ConfirmDialog } from '@/design-system/primitives/Dialog/ConfirmDialog';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/design-system/primitives/Dialog/Dialog';
import { Form, FormField, FormSubmitButton } from '@/design-system/primitives/Form/Form';
import { Input } from '@/design-system/primitives/Input/Input';
import { toast } from '@/design-system/primitives/Toast/Toast';
import { useFileUpload } from '@/lib/useFileUpload';
import styles from './PhotoDialogs.module.css';

const PHASE_OPTIONS: Array<{ id: PhaseId; label: string }> = [
  { id: 'Voor', label: 'Voorbereiding' },
  { id: 'Ontw', label: 'Ontwerp' },
  { id: 'Verg', label: 'Vergunningen' },
  { id: 'Aanb', label: 'Aanbesteding' },
  { id: 'Ruw', label: 'Ruwbouw' },
  { id: 'Wwd', label: 'Wind-/waterdicht' },
  { id: 'Tech', label: 'Technieken' },
  { id: 'Afw', label: 'Afwerking' },
  { id: 'Opl', label: 'Oplevering' },
  { id: 'Naz', label: 'Nazorg' },
];

const MAX_BYTES = 25 * 1024 * 1024;

type UploadState =
  | { kind: 'idle' }
  | { kind: 'uploading'; current: number; total: number }
  | { kind: 'done'; uploaded: number; failed: number };

export function PhotoUploadDialog({
  open,
  onOpenChange,
  defaultPhaseId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultPhaseId?: PhaseId;
}) {
  const upload = useUploadPhoto();
  const picker = useFileUpload({ accept: 'image/*', multiple: true, maxBytes: MAX_BYTES });
  const [phaseId, setPhaseId] = useState<PhaseId>(defaultPhaseId ?? 'Ruw');
  const [room, setRoom] = useState('');
  const [caption, setCaption] = useState('');
  const [state, setState] = useState<UploadState>({ kind: 'idle' });

  const close = (next: boolean) => {
    if (!next) {
      picker.reset();
      setRoom('');
      setCaption('');
      setState({ kind: 'idle' });
    }
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (picker.files.length === 0) return;
    setState({ kind: 'uploading', current: 0, total: picker.files.length });
    let failed = 0;
    for (let i = 0; i < picker.files.length; i++) {
      const file = picker.files[i];
      try {
        await upload.mutateAsync({
          file,
          phaseId,
          ...(room.trim() ? { room: room.trim() } : {}),
          ...(caption.trim() ? { caption: caption.trim() } : {}),
        });
      } catch {
        failed += 1;
      }
      setState({ kind: 'uploading', current: i + 1, total: picker.files.length });
    }
    const uploaded = picker.files.length - failed;
    setState({ kind: 'done', uploaded, failed });
    if (failed === 0) {
      toast.success(`${uploaded} foto${uploaded === 1 ? '' : "'s"} geüpload.`);
      close(false);
    }
  };

  const isBusy = state.kind === 'uploading';

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogTitle>Foto's toevoegen</DialogTitle>
        <DialogDescription>
          Sleep foto's hier of kies ze handmatig. EXIF-locatie wordt server-side gestript.
        </DialogDescription>
        <DialogBody>
          <input {...picker.inputProps} />
          <button
            type="button"
            {...picker.dropZoneProps}
            onClick={picker.openPicker}
            className={`${styles.dropZone} ${picker.isDragging ? styles.dropZoneActive : ''}`}
            disabled={isBusy}
          >
            <Icon name="camera" size={18} />
            <span>
              {picker.files.length === 0
                ? "Klik om foto's te kiezen of sleep ze hier"
                : `${picker.files.length} foto${picker.files.length === 1 ? '' : "'s"} klaar`}
            </span>
          </button>
          {picker.error && (
            <div role="alert" className={styles.error}>
              {picker.error}
            </div>
          )}
          {picker.previews.length > 0 && (
            <div className={styles.previewRow}>
              {picker.previews.map((p) => (
                <img
                  key={p.url}
                  src={p.url}
                  alt={p.file.name}
                  width={64}
                  height={64}
                  className={styles.previewThumb}
                />
              ))}
            </div>
          )}

          <label className={styles.fieldLabel} htmlFor="photo-phase">
            Fase
          </label>
          <select
            id="photo-phase"
            value={phaseId}
            onChange={(e) => setPhaseId(e.target.value as PhaseId)}
            className={styles.select}
            disabled={isBusy}
          >
            {PHASE_OPTIONS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>

          <label className={styles.fieldLabel} htmlFor="photo-room">
            Ruimte
          </label>
          <Input
            id="photo-room"
            value={room}
            onChange={(e) => setRoom(e.target.value)}
            placeholder="Bv. kelder noord, badkamer 1ste"
            disabled={isBusy}
          />

          <label className={styles.fieldLabel} htmlFor="photo-caption">
            Bijschrift
          </label>
          <Input
            id="photo-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Korte beschrijving (optioneel)"
            disabled={isBusy}
          />

          {state.kind === 'uploading' && (
            <div className={`mono ${styles.progress}`} aria-live="polite">
              Bezig… {state.current} / {state.total}
            </div>
          )}
          {state.kind === 'done' && state.failed > 0 && (
            <div role="alert" className={styles.error}>
              {state.failed} foto's mislukt; bekijk de meldingen.
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Btn type="button" disabled={isBusy}>
              Annuleer
            </Btn>
          </DialogClose>
          <Btn
            variant="primary"
            onClick={handleSubmit}
            disabled={isBusy || picker.files.length === 0}
          >
            {isBusy ? 'Bezig…' : 'Uploaden'}
          </Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const editSchema = z.object({
  caption: z.string().trim().max(200, 'Maximaal 200 tekens').optional().or(z.literal('')),
  room: z.string().trim().max(80, 'Maximaal 80 tekens').optional().or(z.literal('')),
  phaseId: z.string().min(1, 'Kies een fase'),
});

type EditValues = z.infer<typeof editSchema>;

function emptyToNull(v: string | undefined): string | null {
  if (v === undefined) return null;
  const t = v.trim();
  return t.length === 0 ? null : t;
}

export function PhotoLightbox({
  open,
  onOpenChange,
  photo,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  photo: PhotoDto;
}) {
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const thumb = usePhotoThumbnailUrl(photo.id, 1600);
  const updateMutation = useUpdatePhoto(photo.id);
  const deleteMutation = useDeletePhoto();
  const download = useDownloadPhoto();
  const rotate = useRotatePhoto(photo.id);

  const close = (next: boolean) => {
    if (!next) setEditing(false);
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent width={960} className={styles.lightboxContent}>
        <DialogTitle className={styles.lightboxTitle}>{photo.caption ?? "Foto's"}</DialogTitle>
        <DialogDescription>
          {photo.room ? `${photo.room} · ` : ''}fase {photo.phaseId}
        </DialogDescription>
        <DialogBody>
          <div className={styles.lightboxImageWrap}>
            {thumb.data ? (
              <img
                src={thumb.data}
                alt={photo.caption ?? 'foto'}
                className={styles.lightboxImage}
              />
            ) : (
              <div className={`mono ${styles.lightboxLoading}`}>Laden…</div>
            )}
          </div>

          {editing && (
            <Form<EditValues>
              schema={editSchema}
              defaultValues={{
                caption: photo.caption ?? '',
                room: photo.room ?? '',
                phaseId: photo.phaseId,
              }}
              onSubmit={async (values) => {
                const body: UpdatePhotoCommand = {
                  caption: emptyToNull(values.caption),
                  room: emptyToNull(values.room),
                  phaseId: values.phaseId,
                };
                await updateMutation.mutateAsync(body);
                setEditing(false);
              }}
            >
              <FormField<EditValues> name="caption" label="Bijschrift">
                {(field) => <Input {...field} value={field.value ?? ''} />}
              </FormField>
              <FormField<EditValues> name="room" label="Ruimte">
                {(field) => <Input {...field} value={field.value ?? ''} />}
              </FormField>
              <FormField<EditValues> name="phaseId" label="Fase" required>
                {(field) => (
                  <select
                    {...field}
                    className={styles.select}
                    value={(field.value as string) ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  >
                    {PHASE_OPTIONS.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                )}
              </FormField>
              <div className={`row gap-8 ${styles.editActions}`}>
                <Btn type="button" onClick={() => setEditing(false)}>
                  Annuleer
                </Btn>
                <FormSubmitButton>Opslaan</FormSubmitButton>
              </div>
            </Form>
          )}
        </DialogBody>
        <DialogFooter>
          <Btn
            type="button"
            onClick={() => rotate.mutate({ degrees: -90 })}
            disabled={rotate.isPending}
            aria-label="Roteer links"
          >
            <Icon name="rotateL" size={14} /> Links
          </Btn>
          <Btn
            type="button"
            onClick={() => rotate.mutate({ degrees: 90 })}
            disabled={rotate.isPending}
            aria-label="Roteer rechts"
          >
            <Icon name="rotateR" size={14} /> Rechts
          </Btn>
          <Btn
            type="button"
            onClick={() => download.mutate({ id: photo.id, filename: `${photo.id}.jpg` })}
            disabled={download.isPending}
            aria-label="Download"
          >
            <Icon name="download" size={14} /> Download
          </Btn>
          {!editing && (
            <Btn type="button" onClick={() => setEditing(true)}>
              <Icon name="edit" size={14} /> Wijzig
            </Btn>
          )}
          <Btn
            type="button"
            variant="ghost"
            className="c-bad"
            onClick={() => setConfirmDelete(true)}
            disabled={deleteMutation.isPending}
            aria-label="Verwijder"
          >
            <Icon name="trash" size={14} /> Verwijder
          </Btn>
          <DialogClose asChild>
            <Btn type="button" variant="primary">
              Sluit
            </Btn>
          </DialogClose>
        </DialogFooter>
        <ConfirmDialog
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          title="Foto verwijderen?"
          description="De foto en het originele bestand worden definitief verwijderd."
          confirmLabel="Verwijder"
          destructive
          pending={deleteMutation.isPending}
          onConfirm={async () => {
            try {
              await deleteMutation.mutateAsync(photo.id);
              setConfirmDelete(false);
              close(false);
            } catch {
              // toast surfaced by the hook; keep dialog open so user can retry.
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
